import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];
const STORY_IMG = "https://customer-assets-lqy194kg.emergentagent.net/job_braid-collections/artifacts/l7a9l6m0_DSC05260_Original.webp";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span className="block"
      initial={{ y: "108%" }} whileInView={{ y: 0 }} viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease, delay }}>
      {children}
    </motion.span>
  </span>
);

export const BrandStory = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="story" ref={ref} data-testid="brand-story" className="bg-[#0B0E14] text-[#EDEEF1] overflow-hidden">
      {/* Headline block */}
      <div className="w-full mx-auto px-4 sm:px-8 md:px-12 pt-24 md:pt-36 pb-16 md:pb-24">
        <p className="label text-[#2B6BFF] mb-8">// The Reason</p>
        <h2 className="font-serif-display text-6xl sm:text-8xl lg:text-[9rem] leading-[0.82]">
          <span className="block overflow-hidden">
            <motion.span className="block" initial={{ y: "108%" }} animate={{ y: 0 }} transition={{ duration: 1, ease, delay: 0.15 }}>His fight.</motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span className="block volt" initial={{ y: "108%" }} animate={{ y: 0 }} transition={{ duration: 1, ease, delay: 0.28 }}>Our fight.</motion.span>
          </span>
        </h2>
      </div>

      {/* Story + image */}
      <div className="grid grid-cols-1 md:grid-cols-12 border-t border-white/10">
        <div className="md:col-span-6 relative min-h-[380px] md:min-h-[620px] overflow-hidden border-b md:border-b-0 md:border-r border-white/10">
          <motion.img style={{ y }} src={STORY_IMG} alt="The Alcoa community standing together"
            className="absolute inset-0 w-full h-[116%] object-cover opacity-80" />
          <div className="absolute inset-0 bg-[#0B0E14]/30" />
          <span className="absolute bottom-6 left-6 label text-[#EDEEF1]/80">Alcoa, Tennessee</span>
        </div>

        <div className="md:col-span-6 px-4 sm:px-8 md:px-14 py-16 md:py-24 flex flex-col justify-center gap-8">
          {[
            "Funky Apparel isn't just clothing. It's a statement of strength, unity, and fighting through whatever life puts in front of you.",
            "The brand was created to stand behind Garrison Stephens and his fight against juvenile ALS. Garrison grew up surrounded by sports, friends, family, and the Alcoa community. ALS changed what he's physically able to do — but it didn't take his determination, his personality, or the people standing beside him.",
            "That's what FUNKY means to us: individuality, personality, and being unapologetically yourself. Keep fighting, keep showing up, and never let your circumstances define you.",
          ].map((p, i) => (
            <motion.p key={p.slice(0, 32)}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease, delay: i * 0.08 }}
              className="text-lg md:text-xl leading-relaxed text-[#C7CBD3]">
              {p}
            </motion.p>
          ))}
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="font-serif-display text-2xl md:text-3xl leading-tight pt-2">
            Wear Funky Apparel and you're wearing more than a piece of clothing — <span className="volt">you're wearing a message of support.</span>
          </motion.p>
        </div>
      </div>

      {/* Awareness strip */}
      <div className="w-full mx-auto px-4 sm:px-8 md:px-12 py-10 md:py-14 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="font-bodoni text-3xl sm:text-4xl">STRENGTH · UNITY · AWARENESS · COMMUNITY</p>
        <span className="label text-[#8A8F99]">#HisFightOurFight</span>
      </div>
    </section>
  );
};
